import React, { useEffect, useState } from 'react';
import { Card } from 'reactstrap';
import * as Icon from 'react-feather';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

import AddEditUserModal from './AddEditUserModal';
import AdminHeader from '../../../components/header/AdminHeader';
import CallexLoader from '../../../components/CallexLoader';
import Pagination from '../../../components/form/Pagination';
import DeleteConfirmationModal from '../../../components/Modals/DeleteConfirmationModal';

import {
  userDelete,
  userListGet,
  useUserList
} from '../../../store/slices/tenantSlices/configurationSlice';
import AddViewCrmsModal from './AddViewCrmsModal';
import AddEditProcessModal from './AddEditProcessModal';
import AssignQueueUsersModal from './AssignQueueUsersModal';
import { useUserDetails, useUserRolePermission } from '../../../store/slices/authSlice';
import { checkModulePermission } from '../../../utils/common';

const Users = () => {
  const dispatch = useDispatch();
  const userList = useUserList();
  const user = useUserDetails();
  const userRolePermission = useUserRolePermission();

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openCrmAddModal, setOpenCrmAddModal] = useState(false);
  const [openProcessAddModal, setOpenProcessAddModal] = useState(false);
  const [userId, setUserId] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [isEdit, setIsEdit] = useState(null);
  const [isDeleteId, setIsDeleteId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    search: undefined
  });

  const fetchUserList = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      const payload = { ...filter };
      await dispatch(userListGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  }, 500);

  const deleteUser = async () => {
    try {
      const response = await dispatch(userDelete(isDeleteId)).unwrap();
      if (response) {
        toast.success('Record deleted successfully');
        if (userList?.data?.length === 1) {
          setFilter({ ...filter, page: filter?.page > 1 ? filter.page - 1 : 1 });
        } else {
          fetchUserList();
        }
        setIsDeleteId(null);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchUserList();
  }, [filter]);

  return (
    <div>
      <AdminHeader
        title="Users"
        isExpandSearch
        isAdd
        onAddClick={() => setOpenAddModal(true)}
        addBtnName="Add User"
        searchPlaceholder="Search user"
        search={filter.search}
        isAddDisabled={
          checkModulePermission('is_add', 'Users', userRolePermission)
            ? user?.allowUser === 0 || !user?.allowUser
              ? false
              : user?.allowUser <= userList?.total
            : true
        }
        handelSearch={(e) => setFilter({ ...filter, page: 1, search: e.target.value })}
      />
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : userList?.data?.length > 0 &&
        checkModulePermission('is_view', 'Users', userRolePermission) ? (
        <Card className="p-0 position-relative m-0">
          <div className="daily-report-table" style={{ height: 'calc(100vh - 210px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Agent Name</th>
                  <th>Process Name</th>
                  <th>Status</th>
                  <th>Call mode</th>
                  <th>Manual Call</th>
                  <th>CDRs</th>
                  <th>Auto Callback</th>
                  <th>Internal Transfer</th>
                  {checkModulePermission('is_view', 'Queues', userRolePermission) && (
                    <th className="text-center" style={{ width: '90px' }}>
                      Assign Queue
                    </th>
                  )}
                  <th className="text-center" style={{ width: '90px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {userList?.data?.map((val, index) => {
                  return (
                    <tr
                      key={index}
                      style={{ background: val?.register === 'no' ? '#ff505033' : '#2cabe333' }}>
                      <td>{val?.Username}</td>
                      <td>{val?.agent_name || '-'}</td>
                      <td>{val?.process_name}</td>
                      <td>{val?.status}</td>
                      <td>{val?.callmode}</td>
                      <td>{val?.manualcall?.toString()}</td>
                      <td>{val?.CDRs?.toString()}</td>
                      <td>{val?.autocallback?.toString()}</td>
                      <td>{val?.internaltransfer?.toString()}</td>
                      {checkModulePermission('is_view', 'Queues', userRolePermission) && (
                        <td>
                          <div className="d-flex justify-content-around align-items-center">
                            {val?.process_type &&
                            ![
                              'OutboundAndPredictive',
                              'OutboundAndPreview',
                              'OutboundAndProgressive'
                            ].includes(val?.process_type) ? (
                              <Icon.Users
                                className="cursor-pointer"
                                size={16}
                                onClick={() => setUserId(val?.id)}
                              />
                            ) : (
                              '-'
                            )}
                          </div>
                        </td>
                      )}
                      <td>
                        <div className="d-flex justify-content-around align-items-center">
                          <div
                            className={`cursor-pointer text-info ${
                              !checkModulePermission('is_edit', 'Users', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Edit
                              size={16}
                              onClick={() => {
                                if (checkModulePermission('is_edit', 'Users', userRolePermission)) {
                                  setEditRecord(val);
                                  setIsEdit(true);
                                  setOpenAddModal(true);
                                }
                              }}
                            />
                          </div>
                          <div
                            className={`cursor-pointer text-danger  ${
                              !checkModulePermission('is_delete', 'Users', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Trash
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission('is_delete', 'Users', userRolePermission)
                                ) {
                                  setIsDeleteId(val?.id);
                                }
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={userList?.total}
              limit={userList?.limit}
              page={userList?.page}
              onLimitChange={(val) => {
                setFilter({ ...filter, page: 1, limit: val });
              }}
              onPageClick={(val) => setFilter({ ...filter, page: val })}
              lastPageClick={(val) => setFilter({ ...filter, page: val })}
              firstPageClick={(val) => setFilter({ ...filter, page: val })}
              onRefreshClick={() => fetchUserList()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 170px)' }}>
          {userList?.data?.length > 0 ? 'No preview available' : 'No data available'}
        </Card>
      )}
      <DeleteConfirmationModal
        isCommon={false}
        isOpen={!!isDeleteId}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={() => deleteUser()}
      />
      <AddEditUserModal
        isOpen={openAddModal}
        setOpenProcessAddModal={setOpenProcessAddModal}
        onCloseClick={(apiCall = false) => {
          setOpenAddModal(false);
          setEditRecord(null);
          setIsEdit(false);
          if (apiCall) {
            fetchUserList();
          }
        }}
        editRecord={editRecord}
        isEdit={isEdit}
      />
      <AddEditProcessModal
        isOpen={openProcessAddModal}
        setOpenCrmAddModal={setOpenCrmAddModal}
        onCloseClick={() => {
          setOpenProcessAddModal(false);
        }}
      />
      <AddViewCrmsModal
        isOpen={openCrmAddModal}
        onCloseClick={() => {
          setOpenCrmAddModal(false);
        }}
      />
      <AssignQueueUsersModal
        isOpen={!!userId}
        user_id={userId}
        onCloseClick={() => setUserId(null)}
      />
    </div>
  );
};

export default Users;
